import React, { useRef, useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicLayout, Section, Eyebrow } from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Smartphone, Loader2, CheckCircle2, ArrowRight, FlaskConical } from "lucide-react";

const DONE = ["completed", "complete", "success", "successful", "paid"];
const FAIL = ["failed", "cancelled", "canceled", "rejected", "expired"];

export default function NucleusPay() {
  const { appId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const isTuition = sp.get("type") === "tuition" || window.location.pathname.startsWith("/apply/tuition");
  const { user } = useAuth();
  const isAdmin = user && ["founder", "admin", "staff", "superadmin"].includes(user.role);
  const [adminTest, setAdminTest] = useState(false);
  const [tuition, setTuition] = useState(null);
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [status, setStatus] = useState(null);
  const [msisdn, setMsisdn] = useState("");
  const [country, setCountry] = useState("CM");
  const [correspondent, setCorrespondent] = useState("MTN_MOMO_CMR");
  const [countries, setCountries] = useState([]);
  const mounted = useRef(false);

  useEffect(() => {
    api.get("/pay/pawapay/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  const ops = (countries.find((c) => c.code === country)?.operators) || [];
  const onCountry = (c) => {
    setCountry(c);
    const co = countries.find((x) => x.code === c);
    setCorrespondent(co?.operators?.[0]?.code || "");
  };

  useEffect(() => {
    api.get("/pay/pawapay/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
    if (isTuition) api.get(`/applications/${appId}/tuition`).then((r) => setTuition(r.data)).catch(() => {});
  }, [isTuition, appId]);

  const pollStatus = (pid) => {
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const { data } = await api.get(`/pay/nucleus/status/${pid}`);
        setStatus(data.status);
        const s = (data.status || "").toLowerCase();
        if (DONE.includes(s)) { toast.success("Paiement confirmé."); setTimeout(() => nav(isTuition ? `/centre/${appId}` : `/assessment/${appId}`), 1200); return; }
        if (FAIL.includes(s)) { toast.error("Paiement échoué ou annulé. Veuillez réessayer."); return; }
      } catch {}
      if (n < 100) setTimeout(tick, 3000); // keep checking ~5 min for the phone confirmation
    };
    tick();
  };

  const payEndpoint = () => (isTuition ? `/applications/${appId}/pay-tuition/start` : (adminTest ? "/admin/pay/nucleus/test-start" : "/pay/nucleus/start"));

  const startCard = async () => {
    setMethod("stripe"); setLoading(true);
    try {
      const payload = isTuition ? { method: "stripe" } : { application_id: appId, method: "stripe", ...(adminTest ? { amount_eur: 1 } : {}) };
      const { data } = await api.post(payEndpoint(), payload);
      setPayment(data);
      if (!mounted.current && window.Stripe && data.stripe) {
        mounted.current = true;
        const stripe = window.Stripe(data.stripe.publishable_key);
        const checkout = await stripe.initEmbeddedCheckout({ clientSecret: data.stripe.client_secret });
        checkout.mount("#checkout");
      }
      pollStatus(data.payment_id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setMethod(null); }
    finally { setLoading(false); }
  };

  const startMomo = async () => {
    if (!msisdn) return toast.error("Entrez votre numéro Mobile Money.");
    setLoading(true);
    try {
      const payload = isTuition ? { method: "pawapay", msisdn, country, correspondent } : { application_id: appId, method: "pawapay", msisdn, country, correspondent, ...(adminTest ? { amount_eur: 1 } : {}) };
      const { data } = await api.post(payEndpoint(), payload);
      setPayment(data); pollStatus(data.payment_id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <Section className="py-12 md:py-16 max-w-2xl">
        <Eyebrow>{isTuition ? "Frais de scolarité · 2026–2027" : "Frais d'admission · 2026–2027"}</Eyebrow>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">{isTuition ? "Réglez vos frais de scolarité" : "Réglez vos frais d'admission"}</h1>
        {isTuition ? (
          <p className="mt-3 text-ink/60" data-testid="tuition-amount">{tuition ? <>Programme : <strong>{tuition.programme_name_fr || tuition.programme_name}</strong> — <strong>{tuition.net} {tuition.currency || "EUR"}</strong>. Nucleus Pay convertit automatiquement le montant dans la devise de votre pays.</> : "Chargement du montant…"}</p>
        ) : (
          <p className="mt-3 text-ink/60">Un montant unique de <strong>97 €</strong>, à régler avant l'évaluation cognitive — l'étape finale de votre candidature.</p>
        )}

        {isAdmin && !isTuition && (
          <label data-testid="admin-test-toggle" className="mt-6 flex items-center gap-2.5 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 cursor-pointer max-w-md">
            <input type="checkbox" checked={adminTest} onChange={(e) => setAdminTest(e.target.checked)} className="h-4 w-4" />
            <FlaskConical className="h-4 w-4" />
            <span>Admin: run a discounted <strong>live</strong> test payment (€1) instead of €97.</span>
          </label>
        )}

        {!method && (
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <button data-testid="pay-method-card" onClick={startCard} className="border border-border rounded-sm p-6 text-left hover:border-heraldic transition-colors">
              <CreditCard className="h-6 w-6 text-heraldic" /><div className="mt-3 font-serif text-xl text-ink">Carte bancaire</div><div className="text-sm text-ink/55">Visa, Mastercard — paiement sécurisé.</div>
            </button>
            <button data-testid="pay-method-momo" onClick={() => setMethod("pawapay")} className="border border-border rounded-sm p-6 text-left hover:border-heraldic transition-colors">
              <Smartphone className="h-6 w-6 text-heraldic" /><div className="mt-3 font-serif text-xl text-ink">Mobile Money</div><div className="text-sm text-ink/55">MTN, Orange — confirmation par code PIN.</div>
            </button>
          </div>
        )}

        {method === "stripe" && (
          <div className="mt-8">
            {loading && <div className="flex items-center gap-2 text-ink/50"><Loader2 className="h-4 w-4 animate-spin" /> Préparation du paiement sécurisé…</div>}
            <div id="checkout" className="mt-2" />
            {status && <p className="mt-4 text-sm text-ink/50">Statut : {status}</p>}
          </div>
        )}

        {method === "pawapay" && !payment && (
          <div className="mt-8 space-y-4 max-w-sm">
            <div>
              <label className="block text-sm text-ink/60 mb-1">Pays</label>
              <select data-testid="momo-country" value={country} onChange={(e) => onCountry(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2 bg-white text-ink focus:border-heraldic outline-none">
                {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-ink/60 mb-1">Opérateur Mobile Money</label>
              <select data-testid="momo-correspondent" value={correspondent} onChange={(e) => setCorrespondent(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2 bg-white text-ink focus:border-heraldic outline-none">
                {ops.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <Input data-testid="momo-msisdn" value={msisdn} onChange={(e) => setMsisdn(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="Ex. 237670000123" className="rounded-sm" />
              <p className="mt-1 text-xs text-ink/45">Numéro avec l'indicatif pays, sans le « + » (ex. 237… pour le Cameroun).</p>
            </div>
            <button data-testid="momo-pay" onClick={startMomo} disabled={loading || !correspondent} className="inline-flex items-center gap-2 bg-heraldic text-white px-7 py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-50">Payer par Mobile Money <ArrowRight className="h-4 w-4" /></button>
          </div>
        )}

        {method === "pawapay" && payment && (
          <div className="mt-8 border border-border rounded-sm p-6">
            <Smartphone className="h-8 w-8 text-heraldic" />
            <p className="mt-3 text-ink/80">{payment.pawapay?.message || "Confirmez le paiement sur votre téléphone (code PIN Mobile Money)."}</p>
            <p className="mt-2 text-sm text-ink/60">Si vous ne recevez pas la demande de code PIN, ouvrez votre application Mobile Money (MTN MoMo / Orange Money…) et validez le paiement en attente. Cette page se met à jour automatiquement dès la confirmation — ne la fermez pas.</p>
            <div className="mt-4 flex items-center gap-2 text-sm text-ink/50">
              {DONE.includes((status || "").toLowerCase()) ? <><CheckCircle2 className="h-4 w-4 text-heraldic" /> Paiement confirmé</> : <><Loader2 className="h-4 w-4 animate-spin" /> En attente de confirmation…</>}
            </div>
          </div>
        )}
      </Section>
    </PublicLayout>
  );
}
